import { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth, type SocialProvider } from '@/hooks/useAuth';
import { usePageMeta } from '@/hooks/usePageMeta';

export default function LoginPage() {
  const { signIn, signInWithOAuth, user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [socialLoading, setSocialLoading] = useState<SocialProvider | null>(null);

  const from = (location.state as { from?: string })?.from || '/';

  // 로그인된 상태면 리디렉트 - side effect는 useEffect에서 처리
  useEffect(() => {
    if (user && !authLoading) {
      navigate(from, { replace: true });
    }
  }, [user, authLoading, navigate, from]);

  usePageMeta({
    title: '로그인 | LottoLab — 로또 번호 생성·통계 분석 서비스',
    description:
      'LottoLab에 로그인하고 저장한 로또 번호를 관리하고, 당첨 확인과 통계 분석 기능을 모두 이용하세요. 카카오·Google 소셜 로그인으로 간편하게 시작할 수 있습니다.',
    keywords:
      '로또 로그인, LottoLab 로그인, 로또 서비스 로그인, 로또 번호 생성 로그인, 로또랩',
    canonical: 'https://lottolab.app/login',
  });

  // 인증 상태 로딩 중
  if (authLoading) {
    return (
      <div className="min-h-screen bg-background-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-10 h-10 mx-auto mb-4 border-2 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-foreground-400 text-sm">로딩 중...</p>
        </div>
      </div>
    );
  }

  // 이미 로그인된 경우 (useEffect에서 리디렉트하지만, 깜빡임 방지용)
  if (user) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!email.trim() || !password.trim()) {
      setError('이메일과 비밀번호를 모두 입력해주세요.');
      return;
    }

    setLoading(true);
    const result = await signIn(email, password);
    setLoading(false);

    if (result.error) {
      setError(result.error);
    } else {
      navigate(from, { replace: true });
    }
  };

  const handleSocialLogin = async (provider: SocialProvider) => {
    setError('');
    setSocialLoading(provider);
    const result = await signInWithOAuth(provider);
    if (result.error) {
      setError(result.error);
      setSocialLoading(null);
    }
  };

  return (
    <div className="min-h-screen bg-background-50 flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2 cursor-pointer">
            <div className="w-10 h-10 rounded-full bg-secondary-500 flex items-center justify-center">
              <i className="ri-flask-line text-white text-sm"></i>
            </div>
            <span className="font-heading text-2xl font-bold"><span className="text-primary-700">Lotto</span><span className="text-secondary-500">Lab</span></span>
          </Link>
        </div>

        {/* Card */}
        <div className="bg-background-50 rounded-2xl p-8 border border-background-200">
          <h1 className="font-heading text-2xl text-foreground-800 mb-1">로그인</h1>
          <p className="text-foreground-400 text-sm mb-5">환영합니다! 다시 만나서 반가워요.</p>

          {error && (
            <div className="mb-5 p-3 rounded-lg bg-accent-50 border border-accent-200 flex items-start gap-2">
              <i className="ri-error-warning-line text-accent-600 text-sm mt-0.5"></i>
              <p className="text-accent-700 text-sm">{error}</p>
            </div>
          )}

          {/* Social Login Buttons */}
          <div className="space-y-2.5 mb-5">
            <button
              onClick={() => handleSocialLogin('kakao')}
              disabled={socialLoading !== null}
              className="w-full py-2.5 rounded-xl font-medium text-sm transition-all duration-200 flex items-center justify-center gap-2.5 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
              style={{ backgroundColor: '#FEE500', color: '#191919' }}
            >
              {socialLoading === 'kakao' ? (
                <span className="w-4 h-4 border-2 border-[#191919]/30 border-t-[#191919] rounded-full animate-spin"></span>
              ) : (
                <i className="ri-kakao-talk-fill text-lg"></i>
              )}
              카카오로 시작하기
            </button>

            <button
              onClick={() => handleSocialLogin('google')}
              disabled={socialLoading !== null}
              className="w-full py-2.5 rounded-xl font-medium text-sm transition-all duration-200 flex items-center justify-center gap-2.5 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap border border-background-300 text-foreground-700 hover:bg-background-50 bg-background-50"
            >
              {socialLoading === 'google' ? (
                <span className="w-4 h-4 border-2 border-foreground-300 border-t-foreground-600 rounded-full animate-spin"></span>
              ) : (
                <i className="ri-google-fill text-lg"></i>
              )}
              Google로 시작하기
            </button>
          </div>

          {/* Divider */}
          <div className="flex items-center gap-3 mb-5">
            <div className="flex-1 h-px bg-background-200"></div>
            <span className="text-xs text-foreground-300 whitespace-nowrap">또는 이메일로 로그인</span>
            <div className="flex-1 h-px bg-background-200"></div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                이메일
              </label>
              <input
                id="email"
                type="email"
                name="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="hello@example.com"
                autoComplete="email"
                className="w-full px-4 py-2.5 rounded-xl border border-background-300 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-300 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-all"
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-foreground-700 mb-1.5">
                비밀번호
              </label>
              <input
                id="password"
                type="password"
                name="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                autoComplete="current-password"
                className="w-full px-4 py-2.5 rounded-xl border border-background-300 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-300 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-all"
              />
            </div>

            <button
              type="submit"
              disabled={loading || socialLoading !== null}
              className="w-full py-2.5 rounded-xl bg-primary-500 hover:bg-primary-600 text-white font-medium text-sm transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
                  로그인 중...
                </span>
              ) : (
                '로그인'
              )}
            </button>
          </form>

          <div className="mt-6 pt-5 border-t border-background-200 text-center">
            <p className="text-foreground-400 text-sm">
              아직 계정이 없으신가요?{' '}
              <Link to="/register" className="text-primary-600 hover:text-primary-700 font-medium cursor-pointer">
                회원가입
              </Link>
            </p>
            <p className="text-foreground-400 text-sm mt-2">
              <Link to="/reset-password" className="text-foreground-400 hover:text-foreground-600 text-xs cursor-pointer">
                비밀번호를 잊으셨나요?
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}